/**
 * <s-caddy> — animated Add to cart button. Wraps the product form's submit button.
 * States (data-state on the element, styled in config/styles.css):
 *   loading — the label pops out of the button and a cart rolls in to catch it
 *   driving — the cart drives off with the label once the add succeeds
 *   added   — check mark and "Added" text, then back to idle
 * Listens to events from the closest <s-product-form>: product-form:adding,
 * product-form:added (holds the mini cart until the cart has driven off) and
 * product-form:failed (springs back).
 */
const ROLL_IN_MS = 650;
const DRIVE_MS = 550;
const ADDED_MS = 1400;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class SCaddy extends HTMLElement {
  #ac;
  #startedAt = 0;
  #idleTimer;

  connectedCallback() {
    const form = this.closest('s-product-form');
    if (!form) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    form.addEventListener('product-form:adding', (event) => {
      if (this.contains(event.detail?.submitter)) this.#start();
    }, opts);
    form.addEventListener('product-form:added', (event) => {
      if (this.dataset.state !== 'loading') return;
      event.detail?.waitUntil?.(this.#complete());
    }, opts);
    form.addEventListener('product-form:failed', () => this.#reset(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#idleTimer);
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #start() {
    clearTimeout(this.#idleTimer);
    this.#startedAt = performance.now();
    this.dataset.state = 'loading';
  }

  async #complete() {
    if (!this.#reducedMotion) {
      const remaining = ROLL_IN_MS - (performance.now() - this.#startedAt);
      if (remaining > 0) await wait(remaining);
      this.dataset.state = 'driving';
      await wait(DRIVE_MS);
    }
    this.dataset.state = 'added';
    const status = this.querySelector('[data-caddy-status]');
    if (status) status.textContent = this.dataset.addedMessage || '';
    this.#idleTimer = setTimeout(() => this.#reset(), ADDED_MS);
  }

  #reset() {
    clearTimeout(this.#idleTimer);
    delete this.dataset.state;
    const status = this.querySelector('[data-caddy-status]');
    if (status) status.textContent = '';
  }
}

if (!customElements.get('s-caddy')) customElements.define('s-caddy', SCaddy);
